import { useEffect, useState, useCallback } from "react";
import logger from "../utils/logger";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import { Calendar as CalendarIcon } from "@phosphor-icons/react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

function formatINR(num) {
  return `₹${Number(num).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function formatDate(d) {
  return d.toISOString().split("T")[0];
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-white/10 p-3 text-xs" style={{ background: "#1A1A1A" }}>
      <p className="text-[#A1A1AA] mb-2">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-[#A1A1AA] capitalize">{p.dataKey.replace(/_/g, " ")}:</span>
          <span className="text-white font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {formatINR(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

const presets = [
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
  { label: "All", days: null },
];

export default function ProfitChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePreset, setActivePreset] = useState("30D");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarStep, setCalendarStep] = useState("start"); // "start" or "end"
  const ws = useWebSocket();

  const fetchData = useCallback((start, end) => {
    setLoading(true);
    let url = `${API}/admin/daily-profit`;
    const params = [];
    if (start) params.push(`start_date=${start}`);
    if (end) params.push(`end_date=${end}`);
    if (params.length) url += `?${params.join("&")}`;

    axios
      .get(url, { headers: getAuthHeaders() })
      .then((res) => {
        const formatted = res.data.map((d) => ({
          ...d,
          displayDate: new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
        }));
        setData(formatted);
      })
      .catch((err) => logger.error("Profit data error:", err))
      .finally(() => setLoading(false));
  }, []);

  const applyPreset = useCallback((preset) => {
    setActivePreset(preset.label);
    const end = formatDate(new Date());
    if (preset.days === null) {
      setStartDate(null);
      setEndDate(null);
      fetchData(null, null);
    } else {
      const start = formatDate(new Date(Date.now() - preset.days * 86400000));
      setStartDate(new Date(start));
      setEndDate(new Date(end));
      fetchData(start, end);
    }
  }, [fetchData]);

  useEffect(() => {
    applyPreset(presets[1]); // Default 30D
  }, [applyPreset]);

  // WebSocket: refresh stats
  useEffect(() => {
    if (!ws) return;
    const unsub = ws.subscribe("stats_update", () => {
      const preset = presets.find((p) => p.label === activePreset);
      if (preset) applyPreset(preset);
    });
    return unsub;
  }, [ws, activePreset, applyPreset]);

  const handleCalendarSelect = (date) => {
    if (calendarStep === "start") {
      setStartDate(date);
      setCalendarStep("end");
    } else {
      setEndDate(date);
      setCalendarOpen(false);
      setCalendarStep("start");
      setActivePreset("custom");
      const s = formatDate(startDate || date);
      const e = formatDate(date);
      fetchData(s, e);
    }
  };

  const displayRange = startDate && endDate
    ? `${startDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} - ${endDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`
    : activePreset === "All" ? "All time" : `Last ${activePreset}`;

  return (
    <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="daily-profit-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Daily Profit Growth
          </h3>
          <p className="text-[10px] text-[#A1A1AA] mt-0.5">{displayRange} &middot; {data.length} days</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Preset buttons */}
          <div className="flex items-center rounded-md border border-white/10 overflow-hidden" data-testid="date-range-presets">
            {presets.map((p) => (
              <button
                key={p.label}
                data-testid={`preset-${p.label.toLowerCase()}`}
                onClick={() => applyPreset(p)}
                className={`px-3 py-1.5 text-[10px] font-bold transition-colors ${
                  activePreset === p.label
                    ? "bg-[#00E5FF] text-black"
                    : "text-[#A1A1AA] hover:text-white hover:bg-white/5"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Calendar picker */}
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                data-testid="date-range-calendar-btn"
                onClick={() => { setCalendarStep("start"); setCalendarOpen(true); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[10px] transition-colors ${
                  activePreset === "custom"
                    ? "border-[#00E5FF]/30 bg-[#00E5FF]/10 text-[#00E5FF]"
                    : "border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/20"
                }`}
              >
                <CalendarIcon size={12} />
                Custom
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0 border-white/10"
              style={{ background: "#1A1A1A" }}
              align="end"
            >
              <div className="p-3 border-b border-white/10">
                <p className="text-[10px] text-[#00E5FF] font-bold uppercase tracking-wider">
                  {calendarStep === "start" ? "Select Start Date" : "Select End Date"}
                </p>
              </div>
              <Calendar
                mode="single"
                selected={calendarStep === "start" ? startDate : endDate}
                onSelect={handleCalendarSelect}
                disabled={(date) => date > new Date()}
                className="rounded-md"
              />
            </PopoverContent>
          </Popover>

          {/* Legend */}
          <div className="flex items-center gap-3 text-[10px] ml-2">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#00E5FF]" />
              <span className="text-[#A1A1AA]">Total Profit</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#10B981]" />
              <span className="text-[#A1A1AA]">Joining Fees</span>
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="h-[280px] flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
            <defs>
              <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="joiningGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 10, fill: "#A1A1AA" }}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#A1A1AA" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="total_profit" stroke="#00E5FF" strokeWidth={2} fill="url(#profitGrad)" />
            <Area type="monotone" dataKey="joining_fees" stroke="#10B981" strokeWidth={1.5} fill="url(#joiningGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
